import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RestaurantStoryAnimation.css';

/* === Kurzgesagt palette === */
const TEAL = '#1BC7B1';
const ORANGE = '#FF6A00';
const BLUE = '#6C7AE0';
const PINK = '#E85D75';
const EYE = '#1A1A2E';
const W = '#FFFFFF';
const DK = '#2D2D3A';
const SK = '#D4956B';
const HAIR = '#3D2B1F';

/* === Restaurant Owner character === */
function OwnerPerson({ animate }) {
    return (
        <svg viewBox="0 0 180 310" className="rs-char">
            <motion.g
                initial={{ y: 20, opacity: 0 }}
                animate={animate ? { y: 0, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
                {/* Shadow */}
                <ellipse cx="90" cy="296" rx="40" ry="8" fill="rgba(0,0,0,0.08)" />
                {/* Left arm */}
                <rect x="32" y="102" width="24" height="50" rx="12" fill={ORANGE} />
                <rect x="34" y="142" width="20" height="36" rx="10" fill={SK} />
                <ellipse cx="44" cy="180" rx="10" ry="9" fill={SK} />
                {/* Body */}
                <rect x="50" y="92" width="80" height="92" rx="14" fill={ORANGE} />
                <rect x="94" y="92" width="36" height="92" rx="10" fill="rgba(0,0,0,0.06)" />
                {/* Apron */}
                <rect x="58" y="110" width="64" height="70" rx="8" fill={W} />
                <rect x="58" y="110" width="64" height="8" rx="4" fill="#e8e4e0" />
                {/* Pocket */}
                <rect x="73" y="145" width="34" height="22" rx="4" fill="#f0ece8" />
                <rect x="80" y="150" width="8" height="3" rx="1.5" fill={ORANGE} />
                {/* Legs */}
                <rect x="56" y="178" width="28" height="84" rx="12" fill={DK} />
                <rect x="96" y="178" width="28" height="84" rx="12" fill="#252535" />
                {/* Shoes */}
                <ellipse cx="70" cy="266" rx="18" ry="9" fill={EYE} />
                <ellipse cx="110" cy="266" rx="18" ry="9" fill={EYE} />
                {/* Right arm with phone */}
                <motion.g
                    animate={animate ? { rotate: [-2, 2, -2] } : {}}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ transformOrigin: '134px 110px' }}
                >
                    <rect x="122" y="102" width="24" height="46" rx="12" fill={ORANGE} />
                    <rect x="124" y="138" width="20" height="32" rx="10" fill={SK} />
                    <ellipse cx="134" cy="172" rx="10" ry="9" fill={SK} />
                    {/* Phone */}
                    <rect x="124" y="152" width="22" height="36" rx="5" fill="#1A1A2E" />
                    <rect x="127" y="157" width="16" height="24" rx="3" fill="#4A90D9" />
                    <circle cx="135" cy="166" r="3" fill={ORANGE} />
                </motion.g>
                {/* Neck */}
                <rect x="80" y="74" width="20" height="20" rx="4" fill={SK} />
                {/* Head */}
                <ellipse cx="90" cy="48" rx="26" ry="28" fill={SK} />
                <ellipse cx="64" cy="52" rx="5" ry="7" fill={SK} />
                <ellipse cx="116" cy="52" rx="5" ry="7" fill={SK} />
                {/* Hair */}
                <ellipse cx="90" cy="26" rx="28" ry="14" fill={HAIR} />
                <ellipse cx="74" cy="32" rx="12" ry="11" fill={HAIR} />
                {/* Eyes */}
                <circle cx="80" cy="48" r="3.5" fill={EYE} />
                <circle cx="82" cy="46" r="1.2" fill={W} />
                <circle cx="100" cy="48" r="3.5" fill={EYE} />
                <circle cx="102" cy="46" r="1.2" fill={W} />
                {/* Blush */}
                <ellipse cx="72" cy="58" rx="7" ry="3.5" fill="rgba(232,93,117,0.15)" />
                <ellipse cx="108" cy="58" rx="7" ry="3.5" fill="rgba(232,93,117,0.15)" />
                {/* Smile */}
                <path d="M83 60 Q90 68 97 60" stroke={PINK} strokeWidth="2.5" fill="none" strokeLinecap="round" />
            </motion.g>
        </svg>
    );
}

/* === Laptop form === */
function LaptopForm({ animate }) {
    return (
        <motion.svg viewBox="0 0 220 170" className="rs-prop"
            initial={{ scale: 0, opacity: 0 }}
            animate={animate ? { scale: 1, opacity: 1 } : {}}
            transition={{ delay: 0.4, type: 'spring', stiffness: 180, damping: 18 }}
        >
            {/* Shadow */}
            <ellipse cx="110" cy="158" rx="90" ry="8" fill="rgba(0,0,0,0.06)" />
            {/* Laptop screen */}
            <rect x="25" y="8" width="170" height="115" rx="10" fill="#2D2D3A" />
            <rect x="33" y="16" width="154" height="95" rx="6" fill="#363647" />
            {/* Form header */}
            <rect x="45" y="28" width="90" height="10" rx="5" fill={ORANGE} />
            {/* Form fields */}
            <rect x="45" y="46" width="130" height="8" rx="4" fill="rgba(255,255,255,0.12)" />
            <rect x="45" y="60" width="130" height="8" rx="4" fill="rgba(255,255,255,0.12)" />
            <rect x="45" y="74" width="96" height="8" rx="4" fill="rgba(255,255,255,0.12)" />
            {/* Submit btn */}
            <motion.rect x="45" y="90" width="56" height="14" rx="7" fill={TEAL}
                animate={animate ? { opacity: [0.7, 1, 0.7] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
            />
            <text x="73" y="100" textAnchor="middle" fill={W} fontSize="7" fontWeight="700" fontFamily="sans-serif">Enviar</text>
            {/* Progress indicator */}
            <motion.circle cx="188" cy="30" r="6" fill="none" stroke={TEAL} strokeWidth="2"
                strokeDasharray="37.7"
                animate={animate ? { strokeDashoffset: [37.7, 0] } : {}}
                transition={{ delay: 0.8, duration: 3, ease: 'easeInOut' }}
            />
            {/* Laptop base */}
            <rect x="15" y="122" width="190" height="10" rx="5" fill="#3A3A4A" />
            <rect x="75" y="130" width="70" height="4" rx="2" fill="#4A4A5A" />
        </motion.svg>
    );
}

/* === Approval / verification scene === */
function ApprovalScene({ animate }) {
    return (
        <motion.div className="rs-approval-scene"
            initial={{ opacity: 0 }}
            animate={animate ? { opacity: 1 } : {}}
        >
            {/* Big checkmark circle */}
            <motion.svg viewBox="0 0 140 140" className="rs-approval-check"
                initial={{ scale: 0, rotate: -20 }}
                animate={animate ? { scale: 1, rotate: 0 } : {}}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 15 }}
            >
                <circle cx="70" cy="70" r="64" fill={TEAL} />
                <circle cx="70" cy="70" r="54" fill="rgba(255,255,255,0.08)" />
                {/* Checkmark */}
                <motion.path
                    d="M42 70 L60 88 L98 50"
                    stroke={W}
                    strokeWidth="9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={animate ? { pathLength: 1 } : {}}
                    transition={{ delay: 0.7, duration: 0.6, ease: 'easeOut' }}
                />
            </motion.svg>
            {/* Info card below */}
            <motion.div className="rs-approval-card"
                initial={{ y: 20, opacity: 0 }}
                animate={animate ? { y: 0, opacity: 1 } : {}}
                transition={{ delay: 0.5 }}
            >
                <p className="rs-approval-card__title">Verificando tu informacion</p>
                <div className="rs-approval-card__bar">
                    <motion.div className="rs-approval-card__fill"
                        initial={{ width: 0 }}
                        animate={animate ? { width: '100%' } : {}}
                        transition={{ delay: 0.6, duration: 2.5, ease: 'easeInOut' }}
                    />
                </div>
                <div className="rs-approval-card__items">
                    <span className="rs-approval-card__item rs-approval-card__item--done">Datos del negocio</span>
                    <span className="rs-approval-card__item rs-approval-card__item--done">RUC verificado</span>
                    <span className="rs-approval-card__item">Cuenta bancaria</span>
                </div>
            </motion.div>
        </motion.div>
    );
}

/* === Storefront scene — redesigned === */
function StoreFrontScene({ animate }) {
    return (
        <motion.div className="rs-store-scene"
            initial={{ opacity: 0 }}
            animate={animate ? { opacity: 1 } : {}}
        >
            <motion.svg viewBox="0 0 320 240" className="rs-store-svg"
                initial={{ y: 20, opacity: 0 }}
                animate={animate ? { y: 0, opacity: 1 } : {}}
                transition={{ delay: 0.2, type: 'spring', stiffness: 160, damping: 20 }}
            >
                {/* Sky/ground */}
                <rect x="0" y="190" width="320" height="50" rx="0" fill="rgba(0,0,0,0.04)" />

                {/* Building */}
                <rect x="60" y="50" width="200" height="150" rx="10" fill="#FFF5ED" />
                <rect x="60" y="50" width="200" height="150" rx="10" fill="rgba(0,0,0,0.02)" />

                {/* Roof awning */}
                <rect x="48" y="38" width="224" height="20" rx="10" fill={ORANGE} />
                {/* Awning scallops */}
                {[0,1,2,3,4,5,6].map(i => (
                    <ellipse key={i} cx={72 + i * 32} cy="58" rx="16" ry="10"
                        fill={i % 2 === 0 ? ORANGE : '#D85300'} />
                ))}

                {/* Sign board */}
                <rect x="100" y="72" width="120" height="36" rx="8" fill={W} />
                <rect x="100" y="72" width="120" height="36" rx="8" stroke="rgba(0,0,0,0.05)" strokeWidth="1" fill="none" />
                <image href="/logo_jama_sinfondo.png" x="115" y="76" width="90" height="28" />

                {/* Left window */}
                <rect x="76" y="120" width="44" height="36" rx="6" fill="#B8D8F0" />
                <rect x="76" y="120" width="44" height="36" rx="6" fill="rgba(255,255,255,0.3)" />
                <line x1="98" y1="120" x2="98" y2="156" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
                <line x1="76" y1="138" x2="120" y2="138" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
                {/* Curtain */}
                <rect x="78" y="120" width="40" height="8" rx="2" fill="#FFD9B3" />

                {/* Right window */}
                <rect x="200" y="120" width="44" height="36" rx="6" fill="#B8D8F0" />
                <rect x="200" y="120" width="44" height="36" rx="6" fill="rgba(255,255,255,0.3)" />
                <line x1="222" y1="120" x2="222" y2="156" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
                <line x1="200" y1="138" x2="244" y2="138" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
                <rect x="202" y="120" width="40" height="8" rx="2" fill="#FFD9B3" />

                {/* Door */}
                <rect x="135" y="132" width="50" height="68" rx="8" fill="#4A3728" />
                <rect x="135" y="132" width="50" height="68" rx="8" fill="rgba(0,0,0,0.1)" />
                <rect x="141" y="138" width="38" height="34" rx="4" fill="rgba(184,216,240,0.4)" />
                {/* Door handle */}
                <circle cx="175" cy="170" r="3.5" fill="#C9A84C" />
                {/* Welcome mat */}
                <rect x="138" y="194" width="44" height="8" rx="3" fill={TEAL} opacity="0.6" />

                {/* OPEN sign */}
                <motion.g
                    animate={animate ? { opacity: [0.6, 1, 0.6] } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    <rect x="148" y="140" width="24" height="12" rx="3" fill="#2ECC40" />
                    <text x="160" y="149" textAnchor="middle" fill={W} fontSize="6" fontWeight="800" fontFamily="sans-serif">OPEN</text>
                </motion.g>

                {/* Decorative plant left */}
                <rect x="55" y="170" width="14" height="24" rx="4" fill="#D4956B" />
                <ellipse cx="62" cy="166" rx="12" ry="10" fill="#4CAF50" />
                <ellipse cx="56" cy="162" rx="8" ry="7" fill="#66BB6A" />

                {/* Decorative plant right */}
                <rect x="252" y="170" width="14" height="24" rx="4" fill="#D4956B" />
                <ellipse cx="259" cy="166" rx="12" ry="10" fill="#4CAF50" />
                <ellipse cx="265" cy="162" rx="8" ry="7" fill="#66BB6A" />

                {/* Notification bubble — orders coming in */}
                <motion.g
                    animate={animate ? { scale: [1, 1.12, 1], y: [0, -2, 0] } : {}}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.8 }}
                >
                    <rect x="230" y="55" width="64" height="28" rx="14" fill={W} />
                    <rect x="230" y="55" width="64" height="28" rx="14" stroke="rgba(0,0,0,0.06)" strokeWidth="1" fill="none" />
                    <circle cx="246" cy="69" r="8" fill="#FF4444" />
                    <text x="246" y="73" textAnchor="middle" fill={W} fontSize="10" fontWeight="800">3</text>
                    <text x="274" y="73" textAnchor="middle" fill={DK} fontSize="9" fontWeight="700" fontFamily="sans-serif">nuevos</text>
                </motion.g>

                {/* Phone notification floating left */}
                <motion.g
                    initial={{ x: -20, opacity: 0 }}
                    animate={animate ? { x: 0, opacity: 1 } : {}}
                    transition={{ delay: 1.2, duration: 0.5 }}
                >
                    <rect x="10" y="80" width="48" height="52" rx="8" fill={DK} />
                    <rect x="14" y="86" width="40" height="36" rx="4" fill="#363647" />
                    <rect x="18" y="92" width="32" height="4" rx="2" fill={ORANGE} opacity="0.8" />
                    <rect x="18" y="100" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.2)" />
                    <rect x="18" y="107" width="28" height="3" rx="1.5" fill="rgba(255,255,255,0.2)" />
                    <rect x="18" y="114" width="16" height="4" rx="2" fill={TEAL} />
                </motion.g>
            </motion.svg>

            {/* Order cards floating */}
            <div className="rs-store-orders">
                {['Pedido #142', 'Pedido #143'].map((label, i) => (
                    <motion.div key={label} className="rs-store-order"
                        initial={{ x: 40, opacity: 0 }}
                        animate={animate ? { x: 0, opacity: 1 } : {}}
                        transition={{ delay: 1 + i * 0.4, type: 'spring', stiffness: 200, damping: 20 }}
                    >
                        <span className="rs-store-order__dot" />
                        <span className="rs-store-order__label">{label}</span>
                        <span className="rs-store-order__status">Nuevo</span>
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
}

/* === Celebration / confetti scene === */
function CelebrationScene({ animate }) {
    const confettiColors = [ORANGE, TEAL, '#FFE500', PINK, BLUE];
    return (
        <div className="rs-celebration">
            <motion.div className="rs-celebration__badge"
                initial={{ scale: 0 }}
                animate={animate ? { scale: 1 } : {}}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 15 }}
            >
                <img src="/logo_jama_sinfondo.png" alt="JAMA" className="rs-celebration__logo" />
                <p className="rs-celebration__text">Tu negocio esta en JAMA</p>
            </motion.div>
            {animate && (
                <div className="rs-confetti" aria-hidden="true">
                    {[...Array(20)].map((_, i) => (
                        <motion.div key={i}
                            className="rs-confetti__piece"
                            style={{
                                left: `${5 + (i * 19) % 90}%`,
                                background: confettiColors[i % confettiColors.length],
                                borderRadius: i % 3 === 0 ? '50%' : '2px',
                                width: i % 2 === 0 ? '8px' : '6px',
                                height: i % 2 === 0 ? '8px' : '10px',
                            }}
                            initial={{ y: -10, opacity: 1 }}
                            animate={{ y: ['0px', '320px'], opacity: [1, 1, 0], rotate: [0, 360 * (i % 2 === 0 ? 1 : -1)] }}
                            transition={{
                                duration: 2.2 + (i % 3) * 0.5,
                                repeat: Infinity,
                                delay: (i * 0.12) % 2,
                                ease: 'linear',
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* === Scenes config === */
const scenes = [
    { id: 'register', step: 1, title: 'Registra tu negocio',     subtitle: 'Llena el formulario en minutos',     bg: '#FFF5ED', accent: ORANGE },
    { id: 'review',   step: 2, title: 'Revisamos tu info',       subtitle: 'Aprobacion en menos de 24 horas',    bg: '#E8FAF6', accent: TEAL },
    { id: 'live',     step: 3, title: 'Tu local esta en linea',  subtitle: 'Empieza a recibir pedidos',           bg: '#FFF8E1', accent: '#F59E0B' },
    { id: 'grow',     step: 4, title: 'Crece con JAMA',          subtitle: 'Mas clientes, mas ventas',            bg: '#EDE9FE', accent: BLUE },
];

const SCENE_DURATION = 4500;

function SceneContent({ sceneId }) {
    switch (sceneId) {
        case 'register':
            return (
                <div className="rs-layout">
                    <div className="rs-layout__char">
                        <motion.div className="rs-bubble"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.6, duration: 0.3, ease: 'backOut' }}
                        >
                            Quiero registrar mi restaurante!
                        </motion.div>
                        <OwnerPerson animate={true} />
                    </div>
                    <LaptopForm animate={true} />
                </div>
            );
        case 'review':
            return <ApprovalScene animate={true} />;
        case 'live':
            return <StoreFrontScene animate={true} />;
        case 'grow':
            return (
                <div className="rs-layout rs-layout--center">
                    <CelebrationScene animate={true} />
                </div>
            );
        default:
            return null;
    }
}

const sceneVariants = {
    enter: (dir) => ({ x: dir > 0 ? '60%' : '-60%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir < 0 ? '60%' : '-60%', opacity: 0 }),
};

export default function RestaurantStoryAnimation() {
    const [current, setCurrent] = useState(0);
    const [direction, setDirection] = useState(1);
    const [isPlaying, setIsPlaying] = useState(true);

    const goTo = useCallback((i) => {
        setDirection(i > current ? 1 : -1);
        setCurrent(i);
    }, [current]);

    useEffect(() => {
        if (!isPlaying) return;
        const timer = setTimeout(() => {
            setDirection(1);
            setCurrent((current + 1) % scenes.length);
        }, SCENE_DURATION);
        return () => clearTimeout(timer);
    }, [current, isPlaying]);

    const scene = scenes[current];

    return (
        <section
            className="rs-story"
            style={{ '--rs-bg': scene.bg, '--rs-accent': scene.accent }}
            onClick={() => setIsPlaying(p => !p)}
        >
            <div className="rs-story__inner">
                {/* Progress bar */}
                <div className="rs-story__progress">
                    {scenes.map((s, i) => (
                        <div key={s.id} className="rs-story__progress-seg">
                            <motion.div
                                className="rs-story__progress-fill"
                                style={{ background: i <= current ? scene.accent : 'rgba(0,0,0,0.08)' }}
                                initial={false}
                                animate={{ scaleX: i < current ? 1 : i === current ? 1 : 0 }}
                                transition={{ duration: 0.4 }}
                            />
                        </div>
                    ))}
                </div>

                <div className="rs-story__header">
                    <motion.span className="rs-story__step"
                        key={scene.id}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.2, ease: 'backOut' }}
                        style={{ background: `${scene.accent}15`, color: scene.accent }}
                    >
                        Paso {scene.step} de {scenes.length}
                    </motion.span>
                    <motion.h3 className="rs-story__title"
                        key={`t-${scene.id}`}
                        initial={{ y: 12, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.05 }}
                    >
                        {scene.title}
                    </motion.h3>
                    <motion.p className="rs-story__subtitle"
                        key={`s-${scene.id}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.15 }}
                    >
                        {scene.subtitle}
                    </motion.p>
                </div>

                <div className="rs-story__stage">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div key={scene.id} className="rs-scene"
                            custom={direction} variants={sceneVariants}
                            initial="enter" animate="center" exit="exit"
                            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        >
                            <SceneContent sceneId={scene.id} />
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="rs-story__dots">
                    {scenes.map((s, i) => (
                        <button key={s.id}
                            className={`rs-story__dot ${i === current ? 'rs-story__dot--active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); goTo(i); }}
                            aria-label={`Paso ${i + 1}`}
                            style={i === current ? { background: scene.accent } : {}}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
